import type { TimePermissionRequestFilters } from '../types/timePermission';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Default history window is the current calendar year.
 * Backend requires fromDate and toDate; history has no status/type/search filters.
 */
export function createDefaultTimePermissionDateRange(now = new Date()): {
  fromDate: string;
  toDate: string;
} {
  const year = now.getFullYear();
  return {
    fromDate: `${year}-01-01`,
    toDate: `${year}-12-31`,
  };
}

export function createDefaultTimePermissionFilters(
  now = new Date(),
): TimePermissionRequestFilters {
  return createDefaultTimePermissionDateRange(now);
}

export function isValidTimePermissionDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

export function validateTimePermissionDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!isValidTimePermissionDate(fromDate)) {
    return 'From date must use YYYY-MM-DD format.';
  }
  if (!isValidTimePermissionDate(toDate)) {
    return 'To date must use YYYY-MM-DD format.';
  }
  if (fromDate > toDate) {
    return 'The selected date range is invalid.';
  }
  return null;
}

export function normalizeTimePermissionFilters(input: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}): {
  filters: TimePermissionRequestFilters;
  page: number;
  pageSize: number;
} {
  const defaults = createDefaultTimePermissionDateRange();
  const fromDate = input.fromDate?.trim() || defaults.fromDate;
  const toDate = input.toDate?.trim() || defaults.toDate;
  const rangeError = validateTimePermissionDateRange(fromDate, toDate);
  if (rangeError) {
    throw Object.assign(new Error(rangeError), { code: 'INVALID_DATE_RANGE' });
  }

  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 20;
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0 || pageSize > 100) {
    throw Object.assign(new Error('pageSize must be between 1 and 100'), {
      code: 'VALIDATION_ERROR',
    });
  }

  return {
    filters: { fromDate, toDate },
    page,
    pageSize,
  };
}
